import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { Locale, Translation } from 'locale'
import { DialogWindow } from 'view/components/panels/DialogWindow'
import {
  Collection,
  CollectionActions,
  CollectionModifyOrdersArgs,
  CollectionType,
  EntityType,
  NegotiationFieldMeta,
  Order,
  OrderActions,
  OrderAssignToCollectionArgs,
  RfqType,
  ValidationIssue
} from 'api'
import { AssignToCollectionPanel } from './AssignToCollectionPanel'
import { NotificationType } from 'store/notification'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { createOrdersWithCollectionIdGetterSelector } from 'selectors/orderSelectors'
import { availableCollectionsSelector } from '../../../selectors/collectionSelectors'
import { confirmAddMatchingToExisting } from './utils'
import { Decimal } from 'utils'
import { createInvalidValueValidationIssue, getMaxCollectionSize } from 'model'

type ShouldModifyResponse = {
  cancelled: boolean
  rfqType?: RfqType
}

interface AssignToCollectionDialogProps extends ModalConsumerProps {
  orderIds: string[]
  onClose?: () => unknown
  onAssignToCollection: (args: OrderAssignToCollectionArgs) => unknown
  getCollection: (collectionId: string) => Collection | null
  getOrder: (orderId: string) => Order | null
  getOrdersInCollection: (collectionId: string) => Order[]
  onModifyCollectionOrders: (args: CollectionModifyOrdersArgs) => unknown
  availableCollections: Collection[]
}

interface AssignToCollectionDialogState {
  collectionId: string | null
  newCollectionName: string | null
  newCollectionType: CollectionType | null
}

const mapStateToProps = createStructuredSelector({
  getCollection: createEntityGetterSelector(EntityType.COLLECTION),
  getOrder: createEntityGetterSelector(EntityType.ORDER),
  getOrdersInCollection: createOrdersWithCollectionIdGetterSelector,
  availableCollections: availableCollectionsSelector
})

const mapDispatchToProps = {
  onAssignToCollection: OrderActions.assignToCollection,
  onModifyCollectionOrders: CollectionActions.modifyOrders
}

export class AssignToCollectionDialogComponent extends React.Component<AssignToCollectionDialogProps, AssignToCollectionDialogState> {
  state: AssignToCollectionDialogState = {
    newCollectionName: null,
    collectionId: null,
    newCollectionType: null
  }

  render() {
    const { onClose, orderIds } = this.props
    const { collectionId, newCollectionName, newCollectionType } = this.state
    const collectionSizeValidationIssue = this.getCollectionSizeValidationIssue()
    const confirmEnabled = (collectionId || newCollectionName) && collectionSizeValidationIssue.length === 0

    return (
      <DialogWindow
        title={Translation.TC_COLLECTIONS}
        confirmButtonEnabled={!!confirmEnabled}
        onConfirm={this.submitUpdate}
        confirmLabel={Translation.TC_BUTTON_OK}
        onClose={onClose}>
        <AssignToCollectionPanel
          orderIds={orderIds}
          collectionId={collectionId}
          newCollectionName={newCollectionName}
          newCollectionType={newCollectionType}
          validationIssues={collectionSizeValidationIssue}
          disabled={false}
          onUpdate={this.handleUpdate}
        />
      </DialogWindow>
    )
  }

  private handleUpdate = (collectionId: string | null, newCollectionName: string | null, newCollectionType: CollectionType | null) => {
    this.setState({ collectionId, newCollectionName, newCollectionType })
  }

  private getCollectionSizeValidationIssue = (): ValidationIssue[] => {
    const { getCollection, orderIds, getOrdersInCollection } = this.props
    const { collectionId, newCollectionType } = this.state

    const maxSize = getMaxCollectionSize(collectionId ? getCollection(collectionId)?.type : newCollectionType)

    const maxLegCount = collectionId !== null ? Decimal.subtract(maxSize, getOrdersInCollection(collectionId).length) : maxSize

    if (orderIds.length > maxLegCount) {
      return [
        createInvalidValueValidationIssue(
          collectionId === null ? 'newCollectionName' : 'collectionId',
          Locale.translate(Translation.SC_MAX_COLLECTION_SIZE_TOOLTIP, { remainingLegs: maxLegCount, maxLegs: maxSize })
        )
      ]
    }

    return []
  }

  private getShouldModifyCollectionRfqType = async (): Promise<ShouldModifyResponse> => {
    const { orderIds, getCollection, getOrdersInCollection, getOrder, showDialog } = this.props
    const { collectionId } = this.state

    // Its being added to an existing collection
    if (collectionId) {
      // First get all orders
      const collection = getCollection(collectionId)

      if (collection) {
        const ordersInCollection = getOrdersInCollection(collection.id)
        const ordersBeingAdded = orderIds.map(orderId => getOrder(orderId))
        const ordersBeingAddedShareSameRfqType = ordersBeingAdded.every(order => order?.rfqType === ordersBeingAdded[0]?.rfqType)

        if (
          // Only AON collections have requirement for same RFQ type
          collection?.type === CollectionType.ALL_OR_NOTHING &&
          // All new orders have same RFQ type but its different to that of the target collection.
          // We can assume that all orders in an AON collection are of the same RFQ type so pick the first
          (!ordersBeingAddedShareSameRfqType ||
            (ordersInCollection.length > 0 &&
              ordersBeingAdded.length > 0 &&
              ordersBeingAdded[0]?.rfqType !== ordersInCollection[0].rfqType))
        ) {
          const dialogArgs = {
            type: NotificationType.Confirm,
            title: Translation.SC_CANNOT_MIX_RFQ_TYPE,
            message: Translation.SC_CANNOT_MIX_RFQ_TYPE_MESSAGE,
            messageArgs: {
              packageName: collection.name,
              orderRFQTye: ordersBeingAddedShareSameRfqType ? ordersBeingAdded[0]?.rfqType : Locale.translate(Translation.SC_MULTIPLE),
              packageRfqType: ordersInCollection[0].rfqType
            },
            buttons: [Translation.TC_BUTTON_CANCEL, Translation.TC_RFQ_TYPE_INDICATIVE, Translation.TC_RFQ_TYPE_FIRM],
            cancelButton: Translation.TC_BUTTON_CANCEL,
            defaultButton: ordersBeingAddedShareSameRfqType
              ? ordersBeingAdded[0]?.rfqType === RfqType.FIRM
                ? Translation.TC_RFQ_TYPE_FIRM
                : Translation.TC_RFQ_TYPE_INDICATIVE
              : undefined
          }

          const dialogResult = await showDialog(dialogArgs)

          switch (dialogResult) {
            case Translation.TC_RFQ_TYPE_FIRM:
              return { rfqType: RfqType.FIRM, cancelled: false }
            case Translation.TC_RFQ_TYPE_INDICATIVE:
              return { rfqType: RfqType.INDICATIVE, cancelled: false }
            default:
              return { cancelled: true }
          }
        }
      }
    }
    return { cancelled: false }
  }

  private submitUpdate = async () => {
    const { orderIds, onModifyCollectionOrders, onAssignToCollection, showDialog, availableCollections } = this.props
    const { collectionId, newCollectionName, newCollectionType } = this.state

    const shouldModifyCollection = await this.getShouldModifyCollectionRfqType()

    if (shouldModifyCollection.cancelled) {
      return
    }

    const addMatchingToExisting = await confirmAddMatchingToExisting(showDialog, availableCollections, newCollectionName, newCollectionType)

    if (addMatchingToExisting.cancelled) {
      return
    }

    onAssignToCollection({
      orderIds,
      collectionId:
        addMatchingToExisting && addMatchingToExisting.matchedCollectionId ? addMatchingToExisting.matchedCollectionId : collectionId,
      newCollectionName: addMatchingToExisting && addMatchingToExisting.matchedCollectionId ? null : newCollectionName,
      collectionType: newCollectionName ? newCollectionType : null
    })

    if (collectionId && shouldModifyCollection.rfqType) {
      onModifyCollectionOrders({
        collectionId,
        updatedValues: {
          [NegotiationFieldMeta.RFQ_TYPE]: shouldModifyCollection.rfqType
        }
      })
    }

    if (this.props.onClose) {
      this.props.onClose()
    }
  }
}

export const AssignToCollectionDialog = connect(mapStateToProps, mapDispatchToProps)(withModal(AssignToCollectionDialogComponent))
